import React from 'react'

import { Map } from 'maplibre-gl'
import bearing from '@turf/bearing'
import bezierSpline from '@turf/bezier-spline'
import destination from '@turf/destination'
import distance from '@turf/distance'
import midpoint from '@turf/midpoint'
import { useSelector } from 'react-redux'
import linestring from 'turf-linestring'
import { selectIsMapReady, selectPolylineBeforeLayerId } from '../../store/map/map.selectors'
import { useEffectWithMapContext } from '../map/mapContext.utils'
import { selectArrivalStep, selectDepartureStep } from './steps/steps.selectors'
import debug from 'debug'
import { MINIMUM_DISTANCE_TO_SHOW_GAP_POLYLINE_IN_KM } from '../map/map.constants'
import { selectCurrentHistoryRouteDone } from '../../store/history/history.selectors'
import { PolylineType } from '../../types/itinerary'
import { MapLibreGLOrNullType } from 'react-mappy-map'
import { LineLayerSpecification } from '@maplibre/maplibre-gl-style-spec'
import { LngLatArrayType } from '../../types/map'

const d = debug('mappy:polyline')

export const getPolylineId = (routeId: number, sectionIdx: number) => `polyline_route_${routeId}_section_${sectionIdx}`

type LinePaintType = Required<LineLayerSpecification>['paint']
type LinePaintKeyType = keyof LinePaintType

export const getPaintStyle = (isSelectedRoute: boolean, hasTraffic: boolean): LinePaintType => {
  if (isSelectedRoute) {
    const lineDashArrayValue = [1.5, 0.5]
    return {
      'line-width': 7,
      'line-opacity': 0.8,
      'line-color': ['get', 'color'],
      'line-dasharray': [
        'match',
        ['get', 'display_style'],
        'dotted',
        ['literal', lineDashArrayValue],
        'dashed',
        ['literal', lineDashArrayValue],
        ['literal', [1, 0]],
      ],
    }
  }

  if (hasTraffic)
    return {
      'line-width': 5,
      'line-opacity': 1,
      'line-color': [
        'let',
        'rgba',
        ['to-rgba', ['to-color', ['get', 'color']]],
        [
          'let',
          'r',
          ['number', ['at', 0, ['var', 'rgba']]],
          'g',
          ['number', ['at', 1, ['var', 'rgba']]],
          'b',
          ['number', ['at', 2, ['var', 'rgba']]],
          'a',
          ['number', ['at', 3, ['var', 'rgba']]],
          [
            'let',
            'avg',
            ['+', ['*', ['var', 'r'], 0.299], ['*', ['var', 'g'], 0.587], ['*', ['var', 'b'], 0.114]],
            ['rgba', ['var', 'avg'], ['var', 'avg'], ['var', 'avg'], ['var', 'a']],
          ],
        ],
      ],
    }

  return {
    'line-width': 5,
    'line-opacity': 1,
    'line-color': '#CCCCCC',
  }
}

const renderOrUpdatePolyline = (map: Map, polyline: PolylineType, beforeLayerId?: string) => {
  const { routeId, sections = [], isSelectedRoute, hasTraffic } = polyline
  const startTime = performance.now()
  sections.forEach((section, sectionIdx: number) => {
    const id = getPolylineId(routeId, sectionIdx)
    const paint = getPaintStyle(isSelectedRoute, hasTraffic)
    const { features } = section

    try {
      if (!map.getLayer(id)) {
        const startAddTime = performance.now()
        map.addLayer(
          {
            id,
            type: 'line',
            source: {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features,
              },
            },
            paint,
          },
          beforeLayerId
        )
        if (d.enabled)
          d(
            'renderOrUpdatePolyline > addLayer',
            id,
            `duration: ${(performance.now() - startAddTime).toFixed(2)}ms for routeId:`,
            routeId
          )
      } else {
        if (isSelectedRoute) {
          const startMoveTime = performance.now()
          map.moveLayer(id, beforeLayerId)
          if (d.enabled)
            d(
              'renderOrUpdatePolyline > moveLayer',
              id,
              `duration: ${performance.now() - startMoveTime}ms for routeId:`,
              routeId
            )
        }
        const paintAttributes: LinePaintKeyType[] = ['line-width', 'line-opacity', 'line-color', 'line-dasharray']
        const startPaintTime = performance.now()

        paintAttributes.forEach((attr) => {
          if (map.getPaintProperty(id, attr) && paint[attr] !== undefined) {
            if (d.enabled) {
              const currentValue = map.getPaintProperty(id, attr)
              d('renderOrUpdatePolyline>setPaintProperty', id, attr, currentValue, '->', paint[attr])
            }
            map.setPaintProperty(id, attr, paint[attr])
          }
        })
        if (d.enabled)
          d(
            'renderOrUpdatePolyline > setPaintProperty',
            id,
            `duration: ${performance.now() - startPaintTime}ms for routeId:`,
            routeId
          )
      }
    } catch (error) {
      console.error('failed to renderOrUpdatePolyline', error)
    }
  })
  if (d.enabled)
    d(`renderOrUpdatePolyline > total duration: ${(performance.now() - startTime).toFixed(2)}ms for routeId:`, routeId)
}

const removePolyline = (map: Map, polyline: PolylineType) => {
  const startTime = performance.now()
  const { routeId, sections = [] } = polyline
  sections.forEach((_section, sectionIdx) => {
    const id = getPolylineId(routeId, sectionIdx)

    try {
      if (map) {
        // map can be undefined (cf expense report route)
        if (map.getLayer(id)) {
          if (d.enabled) d('removePolyline > removeLayer', id)
          map.removeLayer(id)
        }
        if (map.getSource(id)) {
          if (d.enabled) d('removePolyline > removeSource', id)
          map.removeSource(id)
        }
      }
    } catch (error) {
      console.error('failed to remove polyline', error)
    }
  })
  if (d.enabled)
    d(`removePolyline > total duration: ${(performance.now() - startTime).toFixed(2)}ms for routeId:`, routeId)
}

const renderGapPolyline = (map: Map, id: string, start: LngLatArrayType, end: LngLatArrayType) => {
  if (map.getLayer(id)) return

  if (d.enabled) d('renderGapPolyline', id)
  const bearingValue = bearing(start, end)

  const leftSideArc = bearingValue + 90 > 180 ? -180 + (bearingValue + 90 - 180) : bearingValue + 90

  const distanceValue = distance(start, end)
  const midpointValue = midpoint(start, end)
  const destinationValue = destination(midpointValue, distanceValue / 5, leftSideArc)

  const curvedLine = bezierSpline(linestring([start, destinationValue.geometry.coordinates, end]))

  map.addLayer({
    id,
    type: 'line',
    source: {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [...curvedLine.geometry.coordinates],
        },
      },
    },
    layout: {
      'line-cap': 'round',
    },
    paint: {
      'line-color': 'black',
      'line-width': 5,
      'line-dasharray': [0, 2],
    },
  })
}

const removeGapPolyline = (map: MapLibreGLOrNullType, id: string) => {
  try {
    if (map) {
      if (map.getLayer?.(id)) {
        if (d.enabled) d('removeGapPolyline > removeLayer', id)
        map.removeLayer(id)
      }
      if (map.getSource?.(id)) {
        if (d.enabled) d('removeGapPolyline > removeSource', id)
        map.removeSource(id)
      }
    }
  } catch (error) {
    console.error('failed to removeGapPolyline', error)
  }
}

type PolyLinePropsType = {
  polyline: PolylineType
  polylineCount: number
}

const Polyline: React.FC<PolyLinePropsType> = ({ polyline, polylineCount }) => {
  const { isSelectedRoute } = polyline

  const currentHistoryRouteDone = useSelector(selectCurrentHistoryRouteDone)
  const isMapReady = useSelector(selectIsMapReady)
  const beforeLayerId = useSelector(selectPolylineBeforeLayerId)
  const arrivalStep = useSelector(selectArrivalStep)
  const departureStep = useSelector(selectDepartureStep)

  const firstSectionFeatureCoordinates = polyline?.sections?.[0].features?.[0].geometry.coordinates[0]

  const lastSection = polyline?.sections?.[polyline?.sections?.length - 1]
  const lastSectionFeature = lastSection?.features?.[lastSection?.features?.length - 1]

  const lastSectionFeatureCoordinates =
    lastSectionFeature?.geometry?.coordinates[lastSectionFeature?.geometry?.coordinates?.length - 1]

  useEffectWithMapContext(
    (map) => {
      if (!isMapReady) return

      if (!isSelectedRoute) {
        removeGapPolyline(map, 'departure-gap-polyline')
        removeGapPolyline(map, 'arrival-gap-polyline')
      } else {
        if (
          departureStep?.coordinates &&
          firstSectionFeatureCoordinates?.length &&
          !map.getLayer('departure-gap-polyline')
        ) {
          const departureDistance = distance(
            [firstSectionFeatureCoordinates[0], firstSectionFeatureCoordinates[1]],
            [departureStep.coordinates.lng, departureStep.coordinates.lat]
          )

          if (departureDistance > MINIMUM_DISTANCE_TO_SHOW_GAP_POLYLINE_IN_KM) {
            const start: LngLatArrayType = [firstSectionFeatureCoordinates[0], firstSectionFeatureCoordinates[1]]
            const end: LngLatArrayType = [departureStep.coordinates.lng, departureStep.coordinates.lat]
            renderGapPolyline(map, 'departure-gap-polyline', start, end)
          }
        }

        if (
          arrivalStep?.coordinates &&
          lastSectionFeatureCoordinates?.length &&
          !map.getLayer('arrival-gap-polyline')
        ) {
          const arrivalDistance = distance(
            [lastSectionFeatureCoordinates[0], lastSectionFeatureCoordinates[1]],
            [arrivalStep.coordinates.lng, arrivalStep.coordinates.lat]
          )
          if (arrivalDistance > MINIMUM_DISTANCE_TO_SHOW_GAP_POLYLINE_IN_KM) {
            const start: LngLatArrayType = [lastSectionFeatureCoordinates[0], lastSectionFeatureCoordinates[1]]
            const end: LngLatArrayType = [arrivalStep.coordinates.lng, arrivalStep.coordinates.lat]
            renderGapPolyline(map, 'arrival-gap-polyline', start, end)
          }
        }
      }

      renderOrUpdatePolyline(map, polyline, beforeLayerId)
    },
    [isSelectedRoute, isMapReady, currentHistoryRouteDone, polylineCount] // dependencies are very important because we only want to update selected/unselected polylines
  )

  useEffectWithMapContext(
    (map) => () => {
      removeGapPolyline(map, 'departure-gap-polyline')
      removeGapPolyline(map, 'arrival-gap-polyline')
      removePolyline(map, polyline)
    },
    []
  )

  return <></>
}

// webpack:BrowserOnly
export default Polyline
